import React, { useState, useEffect } from "react";
import {
  Paper,
  Box,
  Grid,
  Table,
  TextField,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TablePagination,
  Collapse,
  IconButton,
  FormControl,
  MenuItem,
  InputLabel,
  Select,
  Button,
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { NotificationManager } from "react-notifications";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import { SearchBatch } from "../../services/BatchService";
import {
  formatDateTime,
  formatIncubatorTime,
} from "../../common-utilities/date-utility";
import CommonLoader from "../../vlp/components/common/CommonLoader";
import NewTempBatchManager from "./NewTempBatchManager";

const SearchBatchTable = () => {
  const [location, setLocation] = useState("");
  const [batch, setBatch] = useState("");
  const [batchs, setBatchs] = useState([]);
  const [uniqueBatchs, setUniqueBatchs] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("id");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openRows, setOpenRows] = useState({});
  const [openPopup, setOpenPopup] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);

  useEffect(() => {
    const today = new Date();
    const startDateValue = new Date(today);
    startDateValue.setDate(today.getDate() - 3);
    setStartDate(startDateValue.toISOString().split("T")[0]);

    const endDateValue = new Date(today);
    endDateValue.setDate(today.getDate() + 3);
    setEndDate(endDateValue.toISOString().split("T")[0]);
  }, []);

  useEffect(() => {
    async function GetData() {
      await LoadData();
    }
    GetData();
  }, []);

  useEffect(() => {
    LoadData();
  }, [rowsPerPage]);

  const handleSearchClick = () => {
    if (!startDate || !endDate) {
      NotificationManager.error("Please select valid dates");
      return;
    }
    LoadData();
  };

  const LoadData = async () => {
    setShowLoader(true);

    try {
      let batchData = await SearchBatch(
        page,
        startDate,
        endDate,
        searchText,
        rowsPerPage,
        location,
        batch
      );
      if (batchData) {
        const uniqueBatches = batchData.result.filter(
          (value, index, self) =>
            index === self.findIndex((t) => t.batchId === value.batchId)
        );
        setTotalRecords(batchData.count);
        setBatchs(batchData.result);
        setUniqueBatchs(uniqueBatches);
      }
    } catch (err) {
      NotificationManager.error(
        "Unable to get the data. Error " + err,
        "Error"
      );
    }
    setShowLoader(false);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page
  };

  const handleLocationChange = (e) => {
    const selectedLocation = e.target.value.trim();
    setLocation(selectedLocation);
  };

  const handleBatchChange = (e) => {
    const selectedBatch = e.target.value.trim();
    setBatch(selectedBatch);
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
    const sortedData = [...uniqueBatchs].sort((a, b) => {
      if (a[property] < b[property]) return isAsc ? -1 : 1;
      if (a[property] > b[property]) return isAsc ? 1 : -1;
      return 0;
    });
    setUniqueBatchs(sortedData);
  };

  const handleEditClick = (batchId) => {
    console.log("Edit batch with ID:", batchId);
    const batchToEdit = batchs.filter((batch) => batch.batchId === batchId);
    if (batchToEdit) {
      setSelectedBatch(batchToEdit);
      setOpenPopup(true);
    }
  };

  const handleViewClick = (batchId) => {
    setOpenRows((prev) => ({
      ...prev,
      [batchId]: !prev[batchId], // Toggle the current batchId
    }));
  };

  const handleDeleteClick = (batchId) => {
    console.log("Delete batch with ID:", batchId);
  };

  const locations = ["Madison 1", "Madison 2", "Madison 3"];

  const renderActions = (orderId) => (
    <div style={{ minWidth: "120px" }}>
      <IconButton
        aria-label="edit"
        style={{ color: "orange" }}
        onClick={() => handleEditClick(orderId)}
      >
        <EditIcon />
      </IconButton>
      <IconButton
        aria-label="view"
        style={{ color: "green" }}
        onClick={() => handleViewClick(orderId)}
      >
        <VisibilityIcon />
      </IconButton>
      
    </div>
  );

  return (
    <>
      <div className="vlp-component-container">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span className="title">Search Batch</span>
        </div>
        <hr />
        <Grid
          container
          spacing={2}
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "flex-end",
            marginBottom: 2,
          }}
        >
          {/* Search Criteria */}
          <Grid item xs={12} sm={6} md={4} lg={2.5}>
            <TextField
              label="Search Criteria"
              variant="outlined"
              fullWidth
              size="small"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </Grid>

          {/* From Date */}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField
              label="From Date"
              type="date"
              variant="outlined"
              fullWidth
              size="small"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* To Date */}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <TextField
              label="To Date"
              type="date"
              variant="outlined"
              fullWidth
              size="small"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          {/* Analysis Type Dropdown */}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Analysis Type</InputLabel>
              <Select
                value={batch}
                onChange={handleBatchChange}
                label="Analysis Type"
              >
                <MenuItem value="">
                  <em>All analysis type</em>
                </MenuItem>
                <MenuItem value="HPC">HPC (Pool)</MenuItem>
                <MenuItem value="TCDW">TC (DW)</MenuItem>
                <MenuItem value="TCPOOL">TC (Pool)</MenuItem>
                <MenuItem value="Ecoli">E.coli</MenuItem>
                <MenuItem value="HPCDW">HPC (DW)</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Location Dropdown */}
          <Grid item xs={12} sm={6} md={3} lg={2}>
            <FormControl fullWidth variant="outlined" size="small">
              <InputLabel>Select location</InputLabel>
              <Select
                value={location}
                onChange={handleLocationChange}
                label="Select location"
              >
                <MenuItem value="">
                  <em>All locations</em>
                </MenuItem>
                {locations.sort().map((loc) => (
                  <MenuItem key={loc} value={loc}>
                    {loc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Search Button */}
          <Grid item xs={12} sm={6} md={3} lg={1.5}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleSearchClick}
              style={{ height: "100%" }}
            >
              Search
            </Button>
          </Grid>
        </Grid>

        <Paper>
          <Box sx={{ width: "99%" }}>
            <TableContainer sx={{ maxHeight: 500 }}>
              <Table stickyHeader  aria-label="batch manager table" size="small">
                <TableHead>
                  <TableRow>
                    {[
                      "batchId",
                      "type",
                      "location",
                      "inTime",
                      "inUser",
                      "outTime",
                      "outUser",
                    ].map((column) => (
                      <TableCell key={column}>
                        <TableSortLabel
                          active={orderBy === column}
                          direction={orderBy === column ? order : "asc"}
                          onClick={() => handleRequestSort(column)}
                        >
                          {column.charAt(0).toUpperCase() + column.slice(1)}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell>Incubator Time</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {uniqueBatchs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((batch) => (
                      <React.Fragment key={batch.batchId}>
                        <TableRow>
                          <TableCell>{batch.batchId}</TableCell>
                          <TableCell>{batch.type}</TableCell>
                          <TableCell>{batch.location}</TableCell>
                          <TableCell>{formatDateTime(batch.inTime)}</TableCell>
                          <TableCell>{batch.inUser}</TableCell>
                          <TableCell>{formatDateTime(batch.outTime)}</TableCell>
                          <TableCell>{batch.outUser}</TableCell>
                          <TableCell>
                            {formatIncubatorTime(batch.inTime, batch.outTime)}
                          </TableCell>
                          <TableCell>{renderActions(batch.batchId)}</TableCell>
                        </TableRow>
                        <TableRow
                          sx={{
                            display: openRows[batch.batchId]
                              ? "table-row"
                              : "none",
                          }}
                        >
                          <TableCell colSpan={9} sx={{ padding: 0 }}>
                            <Collapse
                              in={!!openRows[batch.batchId]}
                              timeout="auto"
                              unmountOnExit
                            >
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Sample ID</TableCell>
                                    <TableCell>Reviewed</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {batchs
                                    .filter(
                                      (sample) =>
                                        sample.batchId === batch.batchId
                                    )
                                    .map((sample) => (
                                      <TableRow key={sample.sampleLabId}>
                                        <TableCell>{sample.id}</TableCell>
                                        <TableCell>
                                          {sample.sampleLabId}
                                        </TableCell>
                                        <TableCell>
                                          {sample.reviewed ? "Yes" : "No"}
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
              component="div"
              count={totalRecords}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>
        </Paper>
      </div>

      <CommonLoader loading={showLoader} />

      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle></DialogTitle>
        <DialogContent>
          <NewTempBatchManager
            location={location}
            batch={batch}
            selectedBatches={selectedBatch}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePopup} color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SearchBatchTable;
